import { updateStore, useStore } from '@app/store';
import { ApiEndPoints, DefaultLocale, QueryKey } from '@constants';
import { deriveCustomFontFromConsentFont } from '@constants/fonts';
import { httpRequest } from '@lib';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const byDisplayOrder = (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0);

// Tags each item with its index in the BE array before sorting/filtering, so the
// capture payload can answer at the same positions the BE sent.
const withSlot = (item, slot) => ({ ...item, slot });

// Flattens the public feedback config into what the three screens render.
const responseDataTransformer = (data) => {
  const config = data.config || {};
  const content = config.content || {};
  const ratingScreen = content.ratingScreen || {};
  const lowScoreScreen = content.lowScoreScreen || {};
  const reasonChips = lowScoreScreen.reasonChips || {};
  const followUps = lowScoreScreen.followUpQuestions || {};

  return {
    requestId: data.requestId,
    requestType: data.requestType,
    uniqueId: data.uniqueId,
    captureUrl: data.captureUrl,
    // Already answered: the form only shows the acknowledgement.
    feedbackSubmitted: Boolean(data.feedbackSubmitted),
    title: config.feedbackFormTitle || '',
    logoUrl: config.branding?.logoUrl || '',
    brandColor: config.branding?.brandColor || '',
    // { fontName, weights } or null; same shape as the consent UI font.
    customFont: deriveCustomFontFromConsentFont(config.branding?.fontConfig),
    ratingStyle: config.rating?.style,
    lowScoreThreshold: config.rating?.lowScoreThreshold ?? null,
    rating: {
      heading: ratingScreen.heading || '',
      question: ratingScreen.ratingQuestion || '',
      buttonText: ratingScreen.buttonText || '',
    },
    lowScore: {
      headerPrompt: lowScoreScreen.headerPrompt || '',
      reasonsEnabled: !!reasonChips.enabled,
      reasonsMandatory: !!reasonChips.mandatory,
      reasons: (reasonChips.reasons || []).map(withSlot).sort(byDisplayOrder)
        .filter((reason) => reason.text)
        .map((reason) => ({ text: reason.text, slot: reason.slot })),
      reasonSlotCount: (reasonChips.reasons || []).length,
      questionsEnabled: !!followUps.enabled,
      questions: (followUps.questions || []).map(withSlot).sort(byDisplayOrder)
        .filter((question) => question.text)
        .map((question) => ({ text: question.text, mandatory: !!question.mandatory, slot: question.slot })),
      questionSlotCount: (followUps.questions || []).length,
      commentPlaceholder: lowScoreScreen.comment?.placeholder || '',
      commentMandatory: !!lowScoreScreen.comment?.mandatory,
    },
    acknowledgementMessage: content.acknowledgementScreen?.message || '',
  };
};

// The page is opened from the BE's signed feedback link; its query string
// (credentialId, signature, requestId, …) is forwarded as is, with `locale`
// replaced by the language picked on the page.
const buildSignedQuery = (locale) => {
  const params = new URLSearchParams(window.location.search);
  if (locale) params.set('locale', locale);
  return params.toString();
};

const fetchFeedbackForm = async (locale) => {
  const { data } = await httpRequest.get(
    `${ApiEndPoints.FETCH_FEEDBACK_FORM}?${buildSignedQuery(locale)}`,
    { ignoreAuthToken: true },
  );
  // No form (e.g. feedback disabled): surface the BE's message on the error screen.
  if (!data?.data) throw Object.assign(new Error(data?.message), { response: { data } });
  const form = responseDataTransformer(data.data);
  const { verifiedLocales } = data.data;
  // customFont replaces the font hydrated from another center's cache (the
  // link's `type` param makes the store load one); verifiedLocales limits the
  // language picker, same as the consent UI.
  updateStore({ customFont: form.customFont, verifiedLocales });

  // The link's locale may not be verified for this form: fall back to English
  // (or the first verified one), which re-fetches through the locale query key.
  if (Array.isArray(verifiedLocales) && verifiedLocales.length > 0 && !verifiedLocales.includes(locale)) {
    useStore.getState().actions.setLocaleAction(
      verifiedLocales.includes(DefaultLocale) ? DefaultLocale : verifiedLocales[0],
    );
  }
  return form;
};

export const useFetchFeedbackFormQuery = (locale) => useQuery({
  queryKey: [QueryKey.PUBLIC_FEEDBACK, window.location.search, locale],
  queryFn: () => fetchFeedbackForm(locale),
  // Keep the current form on screen while another language loads.
  placeholderData: keepPreviousData,
});
